/**
 * Asks the user to confirm before leaving the editor with unsaved work.
 *
 * Called from navigation handlers (Vue Router guards, browser
 * `beforeunload`, internal page swaps). When `historyStore.isDirty`
 * is true, returns a promise that resolves only after the user picks
 * continue / cancel in the unsaved-changes dialog. When clean, returns
 * 'continue' immediately so the navigation proceeds without UI.
 */
import { useHistoryStore } from '@/stores/historyStore';
import { useUiStore } from '@/stores/uiStore';
import type { NavigationResult } from '@/types/ui/unsavedChangesGuard';

/**
 * Module-level resolver — lets `respond` (called from the dialog
 * component) finish the promise that `confirmNavigation` returned
 * without prop-drilling the resolver through the dialog.
 */
let resolveNavigation: ((result: NavigationResult) => void) | null = null;

/**
 * Public
 */

export function useUnsavedChangesGuard() {
  function confirmNavigation(): Promise<NavigationResult> {
    const historyStore = useHistoryStore();
    if (!historyStore.isDirty) return Promise.resolve('continue');

    const uiStore = useUiStore();
    uiStore.showUnsavedChangesDialog = true;
    return new Promise(resolve => {
      resolveNavigation = resolve;
    });
  }

  // Called by the unsaved-changes dialog's continue / cancel buttons.
  function respond(result: NavigationResult) {
    const uiStore = useUiStore();
    uiStore.showUnsavedChangesDialog = false;
    resolveNavigation?.(result);
    resolveNavigation = null;
  }

  return { confirmNavigation, respond };
}
